/**
 * Feature E02-F03-T07 — multipart "save project changes" api-client + Query hook.
 *
 * 1:1 port of `../EER app/public/assets/js/project.js → saveProjectChanges`: the
 * deferred field edits + any picked files are sent as ONE multipart PATCH so the
 * server merges `updates` into `projects.data` AND uploads the files in a single
 * request (legacy `apiCall` appended `command` + `session_token` to the form; the
 * shared apiClient does that for us when the body is FormData).
 *
 * The form carries:
 *   - `projectId`              — the target id (parity with the legacy field);
 *   - `updates`                — the collected pending edits, JSON-stringified
 *     (the route parses the string field, `updateProjectBodySchema` accepts
 *     string | object);
 *   - `files[]`                — zero or more picked File blobs.
 *
 * On success the mutation invalidates the project's detail card + the list so the
 * re-read shows the persisted values (DB = source of truth); the caller then
 * clears the localStorage draft and the pending-files queue.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { ProjectUpdates } from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

/** The picked-file shape the save accepts (the model's `PendingFile`). */
export interface SaveFile {
  readonly name: string;
  readonly file?: File;
}

export interface SaveProjectChangesVars {
  readonly projectId: number;
  readonly updates: ProjectUpdates;
  readonly files?: readonly SaveFile[];
}

/**
 * Build the multipart FormData body for a project save (exported for the test +
 * any caller that wants to inspect the payload). Appends `projectId`, the
 * JSON-stringified `updates`, and each picked file under `files[]`.
 */
export function buildSaveForm(vars: SaveProjectChangesVars): FormData {
  const form = new FormData();
  form.append('projectId', String(vars.projectId));
  form.append('updates', JSON.stringify(vars.updates ?? {}));
  for (const f of vars.files ?? []) {
    if (f.file) form.append('files[]', f.file, f.name);
  }
  return form;
}

/** PATCH /projects/:id (multipart) — persist staged edits + upload files. */
export async function saveProjectChanges(vars: SaveProjectChangesVars): Promise<void> {
  const result = await apiClient.patch<unknown>(
    `/projects/${vars.projectId}`,
    buildSaveForm(vars),
    { command: 'update_project' },
  );
  unwrap(result);
}

/**
 * Save mutation. On success invalidates the project's detail card + the projects
 * list so the re-read reflects the persisted values; the caller clears the draft.
 */
export function useSaveProjectChanges(): UseMutationResult<
  void,
  ApiResultError,
  SaveProjectChangesVars
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, SaveProjectChangesVars>({
    mutationFn: (vars) => saveProjectChanges(vars),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: qk.projects.lists() });
    },
  });
}
